// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
// Components, Layouts, Pages
import {
  BaseAvatarGroup,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseTableUser,
  BaseToolbar,
  ChangeValueModal,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { SendPdfTemplate } from '~/components/module/estimate';
// Others
import { useSelector } from 'react-redux';
import useDebounce from '~/utils/hooks/useDebounce';
import { StorageEnum } from '~/utils/enum/base';
import { DateFormatEnum, RoleEnum } from '~/utils/enum/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_ESTIMATE_FILE_NAME_PDF,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { dataTimeZone, formatCurrency, formatDate } from '~/utils/helper/common';
import { IBaseMoreAction, IHeaderCellType } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  IChangeStatusEstimate,
  IEstimate,
  IEstimateDetailUpdate,
  IParamGetListEstimate,
  IPayloadChangeStatusEstimate,
  IPayloadDetailUpdateEstimate,
  IPayloadSendEstimate,
} from '~/utils/interface/estimate';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  changeStatusEstimate,
  deleteEstimate,
  getEstimateDetailUpdate,
  getListEstimate,
  sendEstimate,
} from '~/thunks/estimate/estimateThunk';
import { estimateActions, selectIsRefreshEstimateList } from '~/thunks/estimate/estimateSlice';
import { mockDataChangeStatusEstimate } from '~/utils/mockData/estimate';
import { schema } from './helper';
// Styles, images, icons
import styles from './JobEstimate.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (
  role: string,
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onSendEstimate: (id: string) => void,
  onChangeStatus: (record: IEstimate) => void
): IHeaderCellType<IEstimate>[] => {
  return [
    {
      id: 'customer',
      label: t('table_head_detail_job_title_customer'),
      minWidth: 120,
      render: (record: IEstimate) => {
        return record?.client ? (
          <BaseTableUser
            user={{
              avatar: record.client?.avatarImage,
              firstName: record.client?.firstName,
              lastName: record.client?.lastName,
            }}
          />
        ) : (
          EMPTY_STRING
        );
      },
    },
    {
      id: 'assignees',
      label: t('table_head_detail_job_title_assignee'),
      minWidth: 120,
      render: (record: IEstimate) => {
        return record?.assignees && record?.assignees.length === DEFAULT_NUMBER_ONE ? (
          <BaseTableUser
            user={{
              avatar: record.assignees[DEFAULT_NUMBER_ZERO]?.avatarImage,
              firstName: record.assignees[DEFAULT_NUMBER_ZERO]?.firstName,
              lastName: record.assignees[DEFAULT_NUMBER_ZERO]?.lastName,
            }}
          />
        ) : (
          <BaseAvatarGroup avatars={record?.assignees || []} borderColor='#FFF' max={5} />
        );
      },
    },
    {
      id: 'createdAt',
      label: t('table_head_detail_job_title_created_date'),
      minWidth: 100,
      render: (record: IEstimate) => {
        return record?.createdAt ? formatDate(record.createdAt, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING;
      },
    },
    {
      id: 'total',
      label: t('table_head_detail_job_title_amount'),
      minWidth: 100,
      render: (record: IEstimate) => {
        return formatCurrency(record?.total) || EMPTY_STRING;
      },
    },
    {
      id: 'status',
      label: t('table_head_detail_job_title_status'),
      minWidth: 100,
      render: (record: IEstimate) => {
        return record.status ? <BaseStatus label={record.status} /> : EMPTY_STRING;
      },
    },
    {
      id: 'action',
      label: t('table_head_detail_job_title_action'),
      align: 'left',
      render: (record: IEstimate) => {
        const actions: IBaseMoreAction[] = [
          {
            icon: (
              <img
                src={icons.tableIconActionView}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_view', { ns: 'common/base' }),
            onClick: () => onView(record.id),
          },
          {
            icon: (
              <img
                src={icons.tableIconActionEdit}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_edit', { ns: 'common/base' }),
            onClick: () => onEdit(record.id),
          },
          {
            icon: (
              <img
                src={icons.tableIconActionSendEstimate}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_send_estimate', { ns: 'common/base' }),
            onClick: () => onSendEstimate(record.id),
          },
        ];

        if (role === RoleEnum.ADMIN) {
          actions.push({
            icon: (
              <img
                src={icons.tableIconActionChangeStatus}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_change_status', { ns: 'common/base' }),
            onClick: () => onChangeStatus(record),
          });
        }

        actions.push({
          icon: (
            <img
              src={icons.tableIconActionDelete}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('itemIconImg')}
            />
          ),
          label: t('table_action_delete', { ns: 'common/base' }),
          onClick: () => onDelete(record.id),
          hasOtherColor: true,
        });

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const JobEstimate = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  const { id: jobId } = useParams();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListEstimate = useSelector(selectIsRefreshEstimateList);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [paramObject, setParamObject] = useState<IParamGetListEstimate>({
    page: pageSelected,
    limit: limitPage,
    jobId: jobId,
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [listEstimate, setListEstimate] = useState<IEstimate[]>([]);

  const [estimateSelectedId, setEstimateSelectedId] = useState<string>(EMPTY_STRING);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowChangeStatus, setIsShowChangeStatus] = useState<boolean>(false);
  const [estimateSelected, setEstimateSelected] = useState<IEstimate | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;

    const newParamObject: IParamGetListEstimate = {
      ...paramObject,
      jobId: jobId,
    };

    handleGetListEstimate(newParamObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListEstimate) return;

    handleGetListEstimate(paramObject);
    dispatch(estimateActions.setRefreshEstimateList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListEstimate]);

  useEffect(() => {
    setSearchKey(textSearchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.updateEstimate(id)}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.updateEstimate(id)}`);
        break;
    }
  };

  const handleDelete = (id: string) => {
    setIsShowConfirmDelete(true);
    setEstimateSelectedId(id);
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.estimate}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.estimate}/${id}`);
        break;
    }
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.estimate}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.estimate}/${id}`);
        break;
    }
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleNavigateToFormEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.createEstimate}?jobId=${jobId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.createEstimate}?jobId=${jobId}`);
        break;
    }
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleGetListEstimate = (params: IParamGetListEstimate) => {
    setIsLoading(true);

    dispatch(getListEstimate(params))
      .unwrap()
      .then((resp) => {
        const { responses, pagination } = resp.data;
        if (!responses) return;

        setListEstimate(responses);
        setPagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setEstimateSelectedId(EMPTY_STRING);
  };

  const handleConfirmDeleteEstimate = () => {
    if (!estimateSelectedId) return;

    setIsLoading(true);

    dispatch(deleteEstimate(estimateSelectedId))
      .unwrap()
      .then((res) => {
        dispatch(estimateActions.setRefreshEstimateList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        setIsShowConfirmDelete(false);
      });
  };

  const handleSendEstimate = async (id: string) => {
    try {
      setIsLoading(true);
      const payload: IPayloadDetailUpdateEstimate = {
        estimateId: id,
        params: {
          timeZone: dataTimeZone(),
        },
      };
      const { data } = await dispatch(getEstimateDetailUpdate(payload)).unwrap();

      const pdfBlob = await generatePdfBlob(data);
      if (pdfBlob) {
        const formData = new FormData();
        formData.append('file', pdfBlob, DEFAULT_ESTIMATE_FILE_NAME_PDF);

        const payload: IPayloadSendEstimate = {
          estimateId: id,
          body: formData,
        };
        await dispatch(sendEstimate(payload))
          .unwrap()
          .then((res) => {
            dispatch(estimateActions.setRefreshEstimateList(true));
          })
          .catch((err) => {})
          .finally(() => setIsLoading(false));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const generatePdfBlob = async (dataPdf: IEstimateDetailUpdate): Promise<Blob | null> => {
    try {
      return await pdf(<SendPdfTemplate data={dataPdf} />).toBlob();
    } catch (error) {
      return null;
    }
  };

  const handleChangeStatus = (record: IEstimate) => {
    setIsShowChangeStatus(true);
    setEstimateSelected(record);
  };

  const handleCloseChangeStatus = () => {
    setIsShowChangeStatus(false);
    setEstimateSelected(null);
  };

  const handleSubmitChangeStatus = async (data: IChangeStatusEstimate) => {
    if (!estimateSelected?.id) return;
    const payload: IPayloadChangeStatusEstimate = {
      estimateId: estimateSelected?.id,
      body: data,
    };
    await dispatch(changeStatusEstimate(payload));
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('container')} id='jobEstimatePage'>
      <BaseToolbar textAction={t('button_add_estimate')} onAction={handleNavigateToFormEstimate}>
        {/* <BaseInputSearch
          searchByContent={SearchByFilterContentEnum.NAME}
          value={searchKey}
          onChange={handleSearchChange}
        /> */}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(
            currentRole,
            t,
            handleView,
            handleEdit,
            handleDelete,
            handleSendEstimate,
            handleChangeStatus
          )}
          dataSource={listEstimate || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDeleteEstimate}
        />
      )}

      {isShowChangeStatus && (
        <ChangeValueModal
          title={t('form_change_status_title')}
          defaultValues={{
            status: estimateSelected?.status || EMPTY_STRING,
          }}
          onSubmit={handleSubmitChangeStatus}
          onClose={handleCloseChangeStatus}
          schema={schema(t)}
          fields={[
            {
              name: 'status',
              label: t('form_change_status_label_status'),
              options: mockDataChangeStatusEstimate,
              required: true,
            },
          ]}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default JobEstimate;
